(() => {
  const configNode = document.getElementById('purchase-limit-config');
  if (!configNode || window.UShopPurchaseLimit) return;
  const config = JSON.parse(configNode.textContent);
  const root = window.Shopify?.routes?.root || '/';
  const gates = new Set(), cache = new Map(), pending = new Map();
  let queue = Promise.resolve(), generation = 0;
  const error = (message, code = '') => Object.assign(new Error(message), { code });
  const generic = '暫時無法確認可購數量，請重新確認。';
  const sum = (cart, id) => cart.items.filter(i => String(i.productId) === String(id)).reduce((n, i) => n + i.quantity, 0);
  async function fetchTimed(url, asText = false) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      const data = asText ? await response.text() : await response.json();
      if (!response.ok || (!asText && data.ok !== true)) throw error(!asText && data.message || generic, data.code);
      return data;
    } catch (e) { if (e.code) throw e; throw error(generic); }
    finally { clearTimeout(timer); }
  }
  async function cartSnapshot() {
    const url = new URL(`${root}cart`, location.origin);
    url.searchParams.set('section_id', 'purchase-limit-cart');
    url.searchParams.set('_pl', crypto.randomUUID());
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const html = await fetchTimed(url, true);
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pl-cart]');
    if (!node) throw error(generic);
    const data = JSON.parse(node.textContent);
    if (!Array.isArray(data.items) || data.items.some(i => !i.key || !/^\d+$/.test(i.productId) || !Number.isSafeInteger(i.quantity) || i.quantity < 0)) throw error(generic);
    return data;
  }
  async function limits(ids, fresh = false) {
    ids = [...new Set(ids.map(String))];
    if (!ids.length) return new Map();
    const epoch = generation;
    const missing = ids.filter(id => fresh || !cache.has(id) || Date.now() - cache.get(id).at > 30000);
    for (let index = 0; index < missing.length; index += 20) {
      const chunk = missing.slice(index, index + 20).sort(), key = chunk.join(',');
      if (!pending.has(key)) {
        const url = new URL(config.proxyPath, location.origin);
        if (url.origin !== location.origin || !url.pathname.startsWith('/apps/')) throw error(generic);
        url.pathname = url.pathname.replace(/\/$/, '') + '/limits';
        url.searchParams.set('product_ids', key);
        url.searchParams.set('_pl', crypto.randomUUID());
        const promise = fetchTimed(url).then(data => {
          if (epoch !== generation || !Array.isArray(data.products)) throw error('會員狀態已更新，請重新確認。');
          for (const id of chunk) {
            const p = data.products.find(p => String(p.productId) === id);
            if (!p || !Number.isSafeInteger(p.purchased) || p.purchased < 0 ||
              (p.limit !== null && (!Number.isSafeInteger(p.limit) || p.limit < 1 || !Number.isSafeInteger(p.remaining) || p.remaining !== Math.max(0, p.limit - p.purchased)))) throw error(generic);
          }
          for (const p of data.products) if (chunk.includes(String(p.productId))) cache.set(String(p.productId), { ...p, at: Date.now() });
        }).finally(() => { if (pending.get(key) === promise) pending.delete(key); });
        pending.set(key, promise);
      }
      await pending.get(key);
    }
    if (epoch !== generation || ids.some(id => !cache.has(id))) throw error(generic);
    return new Map(ids.map(id => [id, cache.get(id)]));
  }
  function exclusive(action) {
    const run = () => navigator.locks ? navigator.locks.request('ushop-purchase-cart', action) : action();
    const next = queue.then(run, run); queue = next.catch(() => {}); return next;
  }
  function reportCart(message) {
    let el = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (!el) { el = document.createElement('p'); el.className = 'pl-cart-error'; document.body.append(el); }
    el.setAttribute('role', 'alert'); el.textContent = message;
  }
  function changed() {
    for (const gate of gates) gate.refresh();
    try { channel?.postMessage('changed'); } catch {}
  }
  let channel;
  try { channel = new BroadcastChannel('ushop-purchase-limit'); channel.onmessage = () => invalidate(); } catch {}
  function invalidate() {
    generation++; cache.clear(); pending.clear();
    if (!document.hidden) for (const gate of gates) gate.refresh();
  }
  class PurchaseLimitGate extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true; this.state = 'loading'; this.version = 0;
      this.retry = this.querySelector('[data-pl-retry]');
      this.retry?.addEventListener('click', () => this.refresh(true));
      gates.add(this);
      // One batch for all gates rendered in this turn of the event loop.
      clearTimeout(PurchaseLimitGate.startTimer);
      PurchaseLimitGate.startTimer = setTimeout(async () => {
        const current = [...gates].filter(g => g.state === 'loading' && g.isConnected);
        const versions = new Map(current.map(g => [g, ++g.version]));
        current.forEach(g => g.setState('loading'));
        try {
          const [history, cart] = await Promise.all([limits(current.map(g => g.dataset.productId)), cartSnapshot()]);
          current.forEach(g => { if (g.version === versions.get(g)) g.render(history.get(g.dataset.productId), cart); });
        } catch (e) { current.forEach(g => { if (g.version === versions.get(g)) g.setState(e.code === 'LOGIN_REQUIRED' ? 'login' : 'error', e.message); }); }
      }, 30);
    }
    disconnectedCallback() { gates.delete(this); clearTimeout(this.slowTimer); this.version++; this.ready = false; }
    get owner() { return this.closest('product-form'); }
    get button() { return this.owner?.querySelector('button[type="submit"][name="add"]'); }
    setState(state, message = '') {
      if (!this.isConnected) return;
      this.state = state; this.dataset.state = state;
      const status = this.querySelector('[data-pl-status]');
      if (status) status.textContent = message;
      if (this.retry) this.retry.hidden = state !== 'error';
      const login = this.querySelector('[data-pl-login]'); if (login) login.hidden = state !== 'login';
      const button = this.button;
      if (!button) return; // Remind-me is type=button and keeps its existing behavior.
      if (state !== 'ready') {
        if (!this.ownsDisabled) { this.wasDisabled = button.disabled; this.ownsDisabled = true; }
        button.disabled = true;
        if (!this.label || this.label.parentElement !== button) {
          this.label = document.createElement('span'); this.label.className = 'pl-button-label'; button.append(this.label);
        }
        const labels = { loading: '確認可購數量中…', blocked: '已達購買上限', error: '暫時無法確認', login: '登入後預購' };
        this.label.textContent = labels[state] || labels.error;
        button.dataset.plState = state; button.setAttribute('aria-busy', String(state === 'loading'));
      } else {
        if (this.ownsDisabled) button.disabled = this.wasDisabled;
        this.ownsDisabled = false; delete button.dataset.plState; button.removeAttribute('aria-busy'); this.label?.remove();
      }
      clearTimeout(this.slowTimer);
      if (state === 'loading') this.slowTimer = setTimeout(() => { if (this.state === 'loading' && status) status.textContent = '仍在確認，請稍候。'; }, 3000);
    }
    render(p, cart) {
      if (!p || !this.isConnected) return;
      const remaining = p.limit === null ? null : Math.max(0, p.remaining - sum(cart, this.dataset.productId));
      const input = this.owner?.querySelector('[name="quantity"]');
      if (input) {
        if (remaining === null) input.removeAttribute('max');
        else input.max = String(Math.max(1, remaining));
        if (remaining > 0 && Number(input.value) > remaining) input.value = String(remaining);
      }
      this.setState(remaining === 0 ? 'blocked' : 'ready', p.limit === null ? '' :
        `累計限購 ${p.limit} 件，已購 ${p.purchased} 件，購物車 ${sum(cart, this.dataset.productId)} 件。`);
    }
    async refresh(fresh = false) {
      const version = ++this.version;
      this.setState('loading');
      try {
        const [history, cart] = await Promise.all([limits([this.dataset.productId], fresh), cartSnapshot()]);
        if (version === this.version) this.render(history.get(this.dataset.productId), cart);
      } catch (e) { if (version === this.version) this.setState(e.code === 'LOGIN_REQUIRED' ? 'login' : 'error', e.message); }
    }
  }
  window.UShopPurchaseLimit = {
    enabled: true,
    async add(owner, event, send) {
      const gate = owner.querySelector('purchase-limit-gate');
      if (!gate) return exclusive(send);
      if (gate.state !== 'ready') return;
      const quantity = Number(owner.form.elements.namedItem('quantity')?.value || 1);
      const variant = owner.form.elements.namedItem('id')?.value;
      gate.setState('loading');
      return exclusive(async () => {
        try {
          const ea = owner.querySelector('early-preorder-gate');
          if (ea && !ea.allows()) { ea.verify(); throw error('請先完成優先預購資格確認。'); }
          const [history, cart] = await Promise.all([limits([gate.dataset.productId]), cartSnapshot()]);
          const p = history.get(gate.dataset.productId);
          if (!Number.isSafeInteger(quantity) || quantity < 1) throw error('請輸入有效的購買數量。');
          if (p.limit !== null && sum(cart, gate.dataset.productId) + quantity > p.remaining) {
            gate.render(p, cart); return;
          }
          if (owner.form.elements.namedItem('id')?.value !== variant || Number(owner.form.elements.namedItem('quantity')?.value || 1) !== quantity) throw error('商品或數量已變更，請重新確認。');
          if (ea && !ea.allows()) throw error('請重新確認優先預購資格。');
          gate.setState('ready');
          await send(); changed();
        } catch (e) { gate.setState(e.code === 'LOGIN_REQUIRED' ? 'login' : 'error', e.message || generic); }
      });
    },
    change(owner, line, quantity, name, send) {
      const input = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      const key = input?.dataset.plLineKey;
      return exclusive(async () => {
        try {
          if (!key || !Number.isSafeInteger(Number(quantity)) || Number(quantity) < 0) throw error('購物車已變更，請重新載入。');
          const cart = await cartSnapshot(), index = cart.items.findIndex(i => i.key === key), item = cart.items[index];
          if (!item) throw error('購物車已變更，請重新載入。');
          if (Number(quantity) > item.quantity && item.limit > 0) {
            const p = (await limits([item.productId])).get(item.productId);
            if (p.limit !== null && sum(cart, item.productId) - item.quantity + Number(quantity) > p.remaining) throw error(`此商品累計限購 ${p.limit} 件，請減少數量。`);
          }
          await send(index + 1, Number(quantity), name); changed();
        } catch (e) { if (input) input.value = input.getAttribute('value'); reportCart(e.message || generic); }
      });
    },
  };
  customElements.define('purchase-limit-gate', PurchaseLimitGate);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) invalidate(); });
  window.addEventListener('pageshow', e => { if (e.persisted) invalidate(); });
  let checkoutBusy = false;
  async function checkout(button) {
    if (checkoutBusy) return;
    checkoutBusy = true; const oldDisabled = button?.disabled;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    try {
      await exclusive(async () => {
        const cart = await cartSnapshot(), ids = cart.items.filter(i => i.limit > 0).map(i => i.productId);
        const history = await limits(ids, true);
        for (const [id, p] of history) if (p.limit !== null && sum(cart, id) > p.remaining) throw error(`有商品超過累計購買上限，請調整購物車數量。`);
        location.assign(`${root}checkout`);
      });
    } catch (e) { reportCart(e.message || generic); }
    finally { checkoutBusy = false; if (button) { button.disabled = oldDisabled; button.removeAttribute('aria-busy'); } }
  }
  document.addEventListener('click', e => {
    const button = e.target.closest?.('button[name="checkout"]');
    if (!button) return;
    e.preventDefault(); e.stopImmediatePropagation(); checkout(button);
  }, true);
  document.addEventListener('submit', e => {
    if (e.submitter?.name !== 'checkout') return;
    e.preventDefault(); e.stopImmediatePropagation(); checkout(e.submitter);
  }, true);
})();
